import React, { useState } from 'react';
import { SIDEBAR_ORDER, SIDEBAR_CATEGORIES } from '../../utils/constants';
import './ProfileSidebar.css';
import { UserProfile } from '../../utils/interfaces';
import { useNavigate } from 'react-router-dom';

interface ProfileSidebarProps {
  onTabClick: (category: string) => void;
  profileLoading: boolean;
  userProfile: UserProfile | null;
  isLoggedIn: boolean; // Add isLoggedIn to props
  onAnalyzeProfile: () => void;
  analysisLoading: boolean;
  analysisRun: boolean;
  analysis: any;
  onOpenAnalysisModal: () => void;
  fittingMode: 'basic' | 'advanced';
  onModeChange: (mode: 'basic' | 'advanced') => void;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  onTabClick,
  profileLoading,
  userProfile,
  onAnalyzeProfile,
  analysisLoading,
  analysisRun,
  analysis,
  onOpenAnalysisModal,
  fittingMode,
  onModeChange,
  isLoggedIn, // Destructure isLoggedIn
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/signup');
      return;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.username) {
          navigate(`/bag/${user.username}`);
          return;
        }
      } catch (err) {
        // ignore JSON parse error and fall through to signup
      }
    }

    navigate('/signup');
  };

  const handleTabClick = (tab: string) => {
    onTabClick(tab);
    // Don't close on mobile after selection - let user keep it open
  };

  const handleHeaderClick = () => {
    setIsExpanded(!isExpanded);
  };

  // Basic mode only shows General and Distances
  const getVisibleTabs = () => {
    if (fittingMode === 'basic') {
      return ['General', 'Distances'];
    }
    return SIDEBAR_ORDER;
  };

  const hasClubs = () => {
    if (!userProfile) return false;
    const clubCategories = Object.values(SIDEBAR_CATEGORIES).flat();
    for (const club of clubCategories) {
      if (userProfile[club as keyof UserProfile] && (userProfile[club as keyof UserProfile] as any).model) {
        return true;
      }
    }
    return false;
  };

  const isButtonDisabled = analysisLoading || !hasClubs();
  let totalClubCount = 0;

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-header-clickable" onClick={handleHeaderClick}>
          <div className="sidebar-title-container">
            <h2 className="sidebar-title">Player Profile</h2>
          </div>
          <div className="share-container">
            <button type="button" className="share-link" onClick={handleShareClick}>Share &gt;</button>
          </div>
          <button className="sidebar-toggle" aria-label={isExpanded ? "Close player profile" : "Edit player profile"}>
            {isExpanded ? (
              // Close icon (X)
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                style={{ marginRight: '10px' }}
              >
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/> 
              </svg>
            ) : (
              <span
                style={{
                  background: '#fff',
                  color: '#1976d2',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
                  border: '2px solid #1976d2',
                  padding: '6px',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  marginRight: '6px',
                }}
              >
                <span>Edit Profile</span>
              </span>
            )}
            &nbsp;
          </button>
        </div>
      </div>
      {profileLoading ? (
        <div className="loading-message">
          <p>Loading profile...</p>
        </div>
      ) : (
        <div className={`sidebar-content ${isExpanded ? 'expanded' : ''}`}>
          {/* Basic/Advanced Mode Toggle */}
          <div style={{ 
            display: 'flex', 
            marginBottom: '0.75rem', 
            backgroundColor: '#fafafa', 
            borderRadius: '4px',
            padding: '2px',
            border: '1px solid #e0e0e0',
            fontSize: '0.8rem'
          }}>
            <button
              onClick={() => onModeChange('basic')}
              style={{
                flex: 1,
                padding: '4px 8px',
                border: 'none',
                borderRadius: '3px',
                fontSize: '0.75rem',
                fontWeight: '400',
                cursor: 'pointer',
                backgroundColor: fittingMode === 'basic' ? '#e3f2fd' : 'transparent',
                color: fittingMode === 'basic' ? '#1976d2' : '#999',
                transition: 'all 0.2s ease'
              }}
            >
              Basic
            </button>
            <button
              onClick={() => onModeChange('advanced')}
              style={{
                flex: 1,
                padding: '4px 8px',
                border: 'none',
                borderRadius: '3px',
                fontSize: '0.75rem',
                fontWeight: '400',
                cursor: 'pointer',
                backgroundColor: fittingMode === 'advanced' ? '#e3f2fd' : 'transparent',
                color: fittingMode === 'advanced' ? '#1976d2' : '#999',
                transition: 'all 0.2s ease'
              }}
            >
              Advanced
            </button>
          </div>

          <ul className="profile-tabs">
            {getVisibleTabs().map((categoryName, idx) => {
              let hasData = false;
              let clubCount = 0;
              const clubNames = new Set();
              
              if (userProfile) {
                // Special handling for Distances tab in basic mode
                if (categoryName === 'Distances' && fittingMode === 'basic') {
                  // Check if user has any distance data
                  const distanceFields = ['driverDistance', 'sevenIronDistance', 'pitchingWedgeDistance'];
                  hasData = distanceFields.some(field => userProfile[field as keyof UserProfile]);
                } else if (SIDEBAR_CATEGORIES[categoryName]) {
                  // Existing logic for advanced mode categories
                  if (categoryName === 'Driver') {
                    hasData = SIDEBAR_CATEGORIES[categoryName].some((field) => {
                        const value = userProfile[field as keyof UserProfile];
                        return field.includes('Driver') && value !== undefined && value !== null && !(typeof value === 'string')
                      }
                    );
                    if (hasData) {
                      clubCount = 1;
                    };
                    totalClubCount += clubCount;
                  } else if (categoryName === 'Fairway Woods') {
                    SIDEBAR_CATEGORIES[categoryName].forEach((field) => {
                      const value = userProfile[field as keyof UserProfile];
                      if (field.includes('Wood') && value !== undefined && value !== null && !(typeof value === 'string')) {
                        clubNames.add(field);
                      }
                    })
                    clubCount = clubNames.size;
                    hasData = clubCount > 0;
                    totalClubCount += clubCount;
                  } else if (categoryName === 'Utilities') {
                    SIDEBAR_CATEGORIES[categoryName].forEach((field) => {
                      const value = userProfile[field as keyof UserProfile];
                      if ((field.includes('Iron') || field.includes('Hybrid')) && value !== undefined && value !== null && !(typeof value === 'string')) {
                        clubNames.add(field);
                      }
                    })
                    clubCount = clubNames.size;
                    hasData = clubCount > 0;
                    totalClubCount += clubCount;
                  } else if (categoryName === 'Irons') {
                    SIDEBAR_CATEGORIES[categoryName].forEach((field) => {
                      const value = userProfile[field as keyof UserProfile];
                      if ((field.includes('Iron') || field.includes('Wedge')) && value !== undefined && value !== null && !(typeof value === 'string')) {
                        clubNames.add(field);
                      }
                    })
                    clubCount = clubNames.size;
                    hasData = clubCount > 0;
                    totalClubCount += clubCount;
                  } else if (categoryName === 'Wedges') {
                    SIDEBAR_CATEGORIES[categoryName].forEach((field) => {
                      const value = userProfile[field as keyof UserProfile];
                      if (field.includes('Wedge') && value !== undefined && value !== null && !(typeof value === 'string')) {
                        clubNames.add(field);
                      }
                    })
                    clubCount = clubNames.size;
                    hasData = clubCount > 0;
                    totalClubCount += clubCount;
                  } else if (categoryName === 'Putter') {
                    SIDEBAR_CATEGORIES[categoryName].forEach((field) => {
                      const value = userProfile[field as keyof UserProfile];
                      if (field.includes('Putter') && value !== undefined && value !== null && !(typeof value === 'string')) {
                        clubNames.add(field);
                      }
                    })
                    clubCount = clubNames.size;
                    hasData = clubCount > 0;
                    totalClubCount += clubCount;
                  }
                }
              }
              return (
                <React.Fragment key={categoryName}>
                  <li
                    className="tab profile-tab-btn"
                    onClick={() => handleTabClick(categoryName)}
                  >
                    {categoryName}
                    {hasData && categoryName !== 'Distances' && (
                      <span
                        style={{
                          fontSize: '1.0em',
                          fontStyle: 'italic',
                          color: '#bbb',
                          verticalAlign: 'middle',
                          float: 'right',
                          paddingRight: '7px',
                          paddingLeft: '7px',
                        }}
                        aria-label="Completed"
                        title={`${categoryName} section has ${clubCount} clubs`}
                      >
                        {clubCount}
                      </span>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
            
            {/* Show "Bag Score" tab if analysis is cached and in advanced mode */}
            {analysisRun && analysis && fittingMode === 'advanced' && (
              <li
                className="tab profile-tab-btn analysis-tab"
                onClick={onOpenAnalysisModal}
              >
                Bag Score
                {analysis && analysis.bagScore && (
                  <span
                    style={{
                      fontSize: '1.0em',
                      fontStyle: 'italic',
                      color: '#bbb',
                      verticalAlign: 'middle',
                      float: 'right',
                      paddingRight: '7px',
                      paddingLeft: '7px',
                    }}
                    aria-label="Bag Score"
                    title={`Your bag score is ${analysis.bagScore} out of 100`}
                  >
                    {analysis.bagScore}/100
                  </span>
                )}
              </li>
            )}
          </ul>
          
          {/* Show analyze button and hint only if analysis is not cached and in advanced mode */}
          {!analysisRun && fittingMode === 'advanced' && (
            <>
              <div className="analyze-button-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <span style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>{`Total Club${totalClubCount === 1 ? '' : 's'}: ${totalClubCount}`}</span>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <button
                    onClick={onAnalyzeProfile}
                    disabled={isButtonDisabled}
                    className="nf-btn nf-btn-secondary"
                    style={{ width: '94%', maxWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    title={!hasClubs() ? "Please add more clubs to your profile to enable analysis" : "Analyze your profile for personalized insights"}
                  >
                    {analysisLoading ? (
                      <>
                        <span
                          className="nf-spinner"
                          style={{
                            width: 18,
                            height: 18,
                            border: '2.5px solid #fff',
                            borderTop: '2.5px solid #1976d2',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'nf-spin 0.8s linear infinite',
                          }}
                        />
                        Analyzing...
                      </>
                    ) : (
                      'Get my Bag Score'
                    )}
                  </button>
                </div>
              </div>
              <div className="recommendation-hint">Add your current clubs to your profile for better recommendations - You may leave any unknown information empty.</div>
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default ProfileSidebar;
